import { useRichTextEditor } from '@levitate/rich-text';
import type { Editor } from '@tiptap/core';
import { Placeholder } from '@tiptap/extensions';
import * as React from 'react';
import { ResizableImage } from '../../../pages/AdminSupportInboxPage/TicketComposer/resizableImage';

export interface DescriptionEditorCallbacks {
	/** Pasted or dropped files: images land inline once uploaded; other files join the attachment list. */
	insertFiles: (files: File[], insertAt?: number) => void;
	onUpdate: (editor: Editor) => void;
}

/**
 * Lean TipTap editor for the new-ticket description: the ticket composer's rich-text foundation
 * (links, lists, inline images, identical paste/drop handling) without the reply-only machinery
 * (mentions, saved replies, formatting bar).
 */
export const useDescriptionEditor = (callbacks: DescriptionEditorCallbacks, initialHtml: string): Editor | null => {
	const callbacksRef = React.useRef(callbacks);
	callbacksRef.current = callbacks;

	return useRichTextEditor({
		initialContent: initialHtml,
		starterKitOptions: {
			blockquote: false,
			code: false,
			codeBlock: false,
			heading: false,
			horizontalRule: false,
			link: {
				openOnClick: false,
				autolink: true,
				defaultProtocol: 'https',
			},
		},
		extensions: [
			Placeholder.configure({ placeholder: 'Describe the issue or request...' }),
			ResizableImage.configure({ inline: false, allowBase64: false }),
		],
		mergeField: false,
		editorProps: {
			attributes: { class: 'support-composer-content' },
			handlePaste: (_view, event) => {
				const files = Array.from(event.clipboardData?.files ?? []);
				if (files.length === 0) return false;
				event.preventDefault();
				callbacksRef.current.insertFiles(files);
				return true;
			},
			handleDrop: (view, event, _slice, moved) => {
				// An internal drag of existing content is a move; let ProseMirror handle it.
				if (moved) return false;
				const files = Array.from(event.dataTransfer?.files ?? []);
				if (files.length === 0) return false;
				event.preventDefault();
				const dropPos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos;
				callbacksRef.current.insertFiles(files, dropPos);
				return true;
			},
		},
		onUpdate: editor => callbacksRef.current.onUpdate(editor),
		shouldRerenderOnTransaction: false,
	});
};
